import { useEffect, useMemo, useRef, useState } from 'react'
import { useChartSave } from '../lib/chartSave.jsx'
import {
  ClipboardList,
  Pencil,
  Plus,
  Trash2,
  Banknote,
  CalendarPlus,
  Timer,
  RotateCcw,
  ChevronDown,
  FilePlus2,
  Truck,
  Lock,
} from 'lucide-react'
import {
  AdultToothChart,
  PedoToothChart,
} from './ToothChart/index.js'
import { tagTypeMeta, normalizeHxTag } from '../lib/chart-helpers.js'
import { toHtml, sanitizeHtml, isHtmlEmpty } from '../lib/html-utils.js'
import RichTextEditor from './RichTextEditor.jsx'
import {
  Badge, Banner, Button, ChoiceChips, ConfirmDialog, DrawerFooter, EmptyState, ErrorState, Field, IconButton, Input, Modal, Textarea,
  useToast,
} from '../kit'
import ToothTaxiPresetPicker, { buildOralExamHtml } from './ToothTaxiPresetPicker.jsx'
import { isToothTaxiTreatment } from '../lib/role.js'
import { forceLogout } from '../lib/auth.js'
import { useAiTranslate, AiTranslateButton, AiTranslation } from './AiTranslate.jsx'

const TAG_TYPES = ['Adult', 'Ped', 'Ortho', 'Full Mouth']
// Each type's existing colour (TAG_TYPE_META in chart-helpers: blue / violet / pink /
// amber) as a CSS colour for the kit's ChoiceChips.
const TAG_TYPE_COLOR = { 'Adult': '#3b82f6', 'Ped': '#8b5cf6', 'Ortho': '#ec4899', 'Full Mouth': '#f59e0b' }

/**
 * Clinical section — view by default, edit when the user has permission and
 * clicks the pencil button. Save flow:
 *   For each entry order i:
 *     POST /api/charts/hx   { id, order: i, hx: { cc, PE, NV, NV_duration, price, note, tag } }
 *     POST /api/charts/tag  { id, order: i, tag, tag_type }
 *   For each deleted entry:
 *     DELETE /api/charts/oral_exam { id, json }
 *
 * Top-level fields (cc / NV / NV_duration / price) are written into hx[0] only.
 */
export default function ClinicalEditor({ chart, treatment, canEdit, lockNote, canCreate, creating, createError, templates = [], onCreate, onSaved, aiSummary, patientId, treatmentId }) {
  const [mode, setMode] = useState('view') // 'view' | 'edit'
  const [draft, setDraft] = useState(() => makeDraft(chart))
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState(null)
  const [showPreset, setShowPreset] = useState(false)
  const chartId = chart?.id
  const toast = useToast()

  // Tooth Taxi Preset is offered only for Tooth Taxi treatments (branch_id === 9).
  // branch_id can sit on the treatment itself or on its linked appointment
  // (appointmentlist), so scan the plausible spots.
  const isToothTaxi = isToothTaxiTreatment(treatment, chart)

  // A new `chart` prop must not wipe UNSAVED edits. It changes when ANOTHER section
  // of this page saves (e.g. Vital Signs' own บันทึก → refreshChart), and resetting
  // here used to throw away a clinical note being typed (found 2026-10-18). While
  // the draft has edits it is kept; save() reads the other sections' data from the
  // fresh `chart` prop, so their new values are still written. A clean editor
  // (or one just saved — mode is back to 'view') resyncs as before.
  const dirtyRef = useRef(false)
  useEffect(() => { if (!dirtyRef.current) setDraft(makeDraft(chart)) }, [chart])

  // unsaved-changes flag → drives the floating save bar (mirrors PatientInfo)
  const baseline = useMemo(() => JSON.stringify(makeDraft(chart)), [chart])
  const dirty = mode === 'edit' && JSON.stringify(draft) !== baseline
  dirtyRef.current = dirty

  // Apply a Tooth Taxi Preset selection: fill Chief Complaint (cc) and the first
  // Oral Exam & Treatment entry's PE (rich text). Both are appended, never
  // destructive — re-applying just adds to what's there.
  function applyPreset(out) {
    const oralHtml = buildOralExamHtml(out)
    setDraft((prev) => {
      const cc = prev.cc?.trim()
        ? (out.chief_complaint ? `${prev.cc}, ${out.chief_complaint}` : prev.cc)
        : (out.chief_complaint || '')
      const entries = prev.entries.length ? prev.entries.map((e) => ({ ...e })) : [blankEntry()]
      if (oralHtml) {
        const first = entries[0]
        first.PE = isHtmlEmpty(first.PE) ? oralHtml : `${first.PE}<br>${oralHtml}`
      }
      return { ...prev, cc, entries }
    })
    setShowPreset(false)
  }

  const startEdit = () => { setMode('edit'); setSaveErr(null) }
  const cancel    = () => { setDraft(makeDraft(chart)); setMode('view'); setSaveErr(null) }

  // Returns true once saved (false on a validation or save error) so the inline
  // button and the page's บันทึกทั้งหมด bar can confirm with a toast.
  async function save() {
    if (!chartId) {
      setSaveErr('ไม่พบ chart_id — เคสนี้ยังไม่มี chart ให้บันทึก')
      toast.error('บันทึกไม่สำเร็จ', { detail: 'เคสนี้ยังไม่มี chart' })
      return false
    }
    // Adult / Ped entries must have at least one tooth selected.
    for (let i = 0; i < draft.entries.length; i++) {
      const e = draft.entries[i]
      const tt = e.tag_type || 'Full Mouth'
      const teeth = Array.isArray(e.tag) ? e.tag : []
      if ((tt === 'Adult' || tt === 'Ped') && teeth.length === 0) {
        const msg = `Entry #${i + 1} (${tt}) ต้องเลือกอย่างน้อย 1 ซี่ก่อนบันทึก`
        setSaveErr(msg)
        toast.error('Clinical ยังบันทึกไม่ได้', { detail: msg })
        return false
      }
    }
    setSaving(true)
    setSaveErr(null)
    try {
      const originalHx = Array.isArray(chart?.hx) ? chart.hx : []
      const now = Date.now()
      const hx = draft.entries.map((e, i) => {
        const prev = originalHx[i] || {}
        const tt   = e.tag_type || 'Full Mouth'
        // For Ortho / Full Mouth, the tag[] always carries the type token.
        // For Adult / Ped, the tag[] holds the selected tooth numbers.
        const tagOut = (tt === 'Ortho' || tt === 'Full Mouth') ? [tt] : (Array.isArray(e.tag) ? e.tag : [])
        const out = {
          PE:          e.PE || '',
          note:        e.note || '',
          canvas:      e.canvas || '',
          tag:         tagOut,
          tag_type:    tt,
          NV_duration: prev.NV_duration ?? 0,
          NV:          prev.NV ?? '',
          price:       prev.price ?? '',
          cc:          prev.cc ?? '',
          creator:     prev.creator ?? null,
          created_at:  prev.created_at ?? now,
        }
        // Vital signs live on hx[].vital_sign (owned by VitalSignsEditor) —
        // carry them through untouched so editing clinical notes never wipes them.
        if (Array.isArray(prev.vital_sign) && prev.vital_sign.length) out.vital_sign = prev.vital_sign
        return out
      })
      // Safety net: if an entry that held vitals was deleted/reordered, re-attach
      // the original readings to hx[0] so they aren't lost on save.
      const origVitals = originalHx.flatMap((e) => (Array.isArray(e?.vital_sign) ? e.vital_sign : []))
      if (origVitals.length && hx.length && !hx.some((e) => Array.isArray(e.vital_sign) && e.vital_sign.length)) {
        hx[0] = { ...hx[0], vital_sign: origVitals }
      }
      const history = {
        cc:          draft.cc || '',
        NV:          draft.NV || '',
        NV_duration: draft.NV_duration === '' || draft.NV_duration == null ? null : Number(draft.NV_duration),
        price:       draft.price || '',
        note:        chart?.history?.note   ?? '',
        canvas:      chart?.history?.canvas ?? '',
      }
      await patchJson(`/api/charts/${chartId}`, { hx, history })
      setMode('view')
      onSaved?.()
      return true
    } catch (e) {
      const msg = e?.message || 'บันทึกไม่สำเร็จ'
      setSaveErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
      return false
    } finally {
      setSaving(false)
    }
  }

  useChartSave('clinical', dirty, save, cancel)
  // AI-Translation — any logged-in user, independent of canEdit (owner decision 2026-10-25).
  const ai = useAiTranslate({ chart, aiSummary, patientId, treatmentId })
  async function saveAndConfirm() {
    if (await save()) toast.success('บันทึกแล้ว')
  }

  return (
    <section className="bg-white border border-slate-200 rounded-2xl">
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <ClipboardList size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Clinical</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">บันทึกทางคลินิก {mode === 'edit' && <span className="text-amber-600 font-medium">· แก้ไขอยู่</span>}</div>
        </div>
        {mode === 'view' && chartId && <AiTranslateButton ai={ai} />}
        {mode === 'view' && canEdit && chartId && (
          <Button variant="soft" size="sm" icon={Pencil} onClick={startEdit}>แก้ไข</Button>
        )}
        {mode === 'view' && !canEdit && chartId && lockNote && (
          <Badge tone="amber" title={lockNote}><Lock size={11} strokeWidth={2.25} />ล็อกการแก้ไข</Badge>
        )}
        {mode === 'view' && !chartId && canCreate && (
          <Button size="sm" icon={Plus} onClick={onCreate} loading={creating}>
            {creating ? 'กำลังสร้าง…' : 'สร้าง chart'}
          </Button>
        )}
        {mode === 'view' && !chartId && !canCreate && (
          <span className="text-[12.5px] text-slate-400 italic">ยังไม่มี chart ในเคสนี้</span>
        )}
        {mode === 'edit' && (
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={cancel} disabled={saving}>ยกเลิก</Button>
            <Button size="sm" onClick={saveAndConfirm} loading={saving}>บันทึก</Button>
          </div>
        )}
      </header>

      <div className="p-4 sm:p-5 space-y-5">
        {(saveErr || createError) && <ErrorState title={saveErr || createError} />}

        {mode === 'view' && !canEdit && chartId && lockNote && (
          <Banner tone="warn" icon={Lock}>{lockNote}</Banner>
        )}

        {/* Tooth Taxi Preset — quick-fill Chief Complaint + Oral Exam & Treatment */}
        {mode === 'edit' && isToothTaxi && (
          <button
            type="button"
            onClick={() => setShowPreset(true)}
            className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border border-brand-200 bg-brand-50/60 hover:bg-brand-50 text-left transition-colors"
          >
            <span className="w-8 h-8 rounded-lg bg-white text-brand-600 ring-1 ring-brand-200 flex items-center justify-center flex-none">
              <Truck size={15} strokeWidth={2} />
            </span>
            <span className="min-w-0">
              <span className="block text-[14px] font-semibold text-brand-800">Tooth Taxi Preset</span>
              <span className="block text-[13px] text-brand-700/70">เลือก Chief Complaint · Diagnosis · Treatment แล้วกรอกให้อัตโนมัติ</span>
            </span>
          </button>
        )}

        {/* Chief complaint */}
        {mode === 'view'
          ? (
            <Block label="Chief Complaint">
              {draft.cc
                ? <div className="text-[15px] text-slate-800 italic">"{draft.cc}"</div>
                : <Muted>ยังไม่ได้บันทึก</Muted>}
            </Block>
          )
          : (
            <Field label="Chief Complaint">
              <Input value={draft.cc || ''} onChange={(e) => setDraft({ ...draft, cc: e.target.value })} placeholder="อาการสำคัญ" />
            </Field>
          )}

        {/* Oral examination entries */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <SectionLabel>Oral Exam & Treatment</SectionLabel>
            {mode === 'edit' && (
              <Button variant="soft" size="sm" icon={Plus} onClick={() => setDraft({ ...draft, entries: [...draft.entries, blankEntry()] })}>
                เพิ่ม entry
              </Button>
            )}
          </div>

          {draft.entries.length === 0
            ? (
              <EmptyState
                icon={ClipboardList}
                title="ยังไม่มี oral examination"
                hint={mode === 'edit' ? 'กดปุ่ม "เพิ่ม entry" เพื่อเริ่มบันทึก' : undefined}
              />
            )
            : (
              <div className="space-y-2.5">
                {draft.entries.map((e, i) => (
                  <HxEntryEditor
                    key={i}
                    index={i}
                    entry={e}
                    mode={mode}
                    templates={templates}
                    onChange={(next) => {
                      const arr = [...draft.entries]
                      arr[i] = next
                      setDraft({ ...draft, entries: arr })
                    }}
                    onRemove={() => {
                      const arr = draft.entries.filter((_, j) => j !== i)
                      setDraft({ ...draft, entries: arr })
                    }}
                  />
                ))}
              </div>
            )}
        </div>

        {/* Footer fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
          {mode === 'view' ? (
            <>
              <ViewField icon={<Banknote size={13} />} label="Treatment Price">
                {draft.price ? <span className="text-[15px] text-slate-800 whitespace-pre-line">{draft.price}</span> : <Muted>—</Muted>}
              </ViewField>
              <ViewField icon={<CalendarPlus size={13} />} label="Next Visit (N/V)">
                {draft.NV ? <span className="text-[15px] text-slate-800">{draft.NV}</span> : <Muted>—</Muted>}
              </ViewField>
              <ViewField icon={<Timer size={13} />} label="N/V Duration">
                {draft.NV_duration !== '' && draft.NV_duration != null ? `${draft.NV_duration} นาที` : <Muted>—</Muted>}
              </ViewField>
            </>
          ) : (
            <>
              <Field label="Treatment Price">
                <Textarea value={draft.price || ''} onChange={(e) => setDraft({ ...draft, price: e.target.value })} placeholder="เช่น ค่าขูดหินปูน 1,200" rows={2} />
              </Field>
              <Field label="Next Visit (N/V)">
                <Input value={draft.NV || ''} onChange={(e) => setDraft({ ...draft, NV: e.target.value })} placeholder="เช่น ติดตามอีก 1 เดือน" />
              </Field>
              <Field label="N/V Duration">
                {({ id }) => (
                  <div className="flex items-center gap-2">
                    <div className="w-24">
                      <Input
                        id={id}
                        type="number"
                        min="0"
                        value={draft.NV_duration ?? ''}
                        onChange={(e) => setDraft({ ...draft, NV_duration: e.target.value })}
                      />
                    </div>
                    <span className="text-[13.5px] text-slate-500">นาที</span>
                  </div>
                )}
              </Field>
            </>
          )}
        </div>

        {mode === 'view' && chartId && <AiTranslation ai={ai} />}
      </div>

      {showPreset && (
        <ToothTaxiPresetModal
          patientName={chart?.patient?.name || chart?.patient_name}
          onApply={applyPreset}
          onCancel={() => setShowPreset(false)}
        />
      )}

    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Tooth Taxi Preset modal — wraps the shared picker; "ใช้ค่านี้" applies it    */
/* into the Chief Complaint + Oral Exam & Treatment fields.                    */
/* -------------------------------------------------------------------------- */
function ToothTaxiPresetModal({ patientName, onApply, onCancel }) {
  const [out, setOut] = useState(() => ({ chief_complaint: '', diagLines: [], treatLines: [] }))

  const nothing = !out.chief_complaint && !out.diagLines?.length && !out.treatLines?.length

  return (
    <Modal
      onClose={onCancel}
      width="lg"
      title="Tooth Taxi Preset"
      subtitle={<>เลือกแล้วกด "ใช้ค่านี้" เพื่อกรอก Chief Complaint และ Oral Exam &amp; Treatment</>}
      dirty={!nothing}
      footer={(
        <DrawerFooter
          onCancel={onCancel}
          onSave={() => onApply(out)}
          saveLabel="ใช้ค่านี้"
          canSave={!nothing}
        />
      )}
    >
      {patientName && <div className="text-[13.5px] text-slate-500 mb-3">ผู้ป่วย: <span className="font-medium text-slate-700">{patientName}</span></div>}

      <ToothTaxiPresetPicker onChange={setOut} />
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Single hx entry editor                                                     */
/* -------------------------------------------------------------------------- */

function HxEntryEditor({ index, entry, mode, templates = [], onChange, onRemove }) {
  const [confirmDelete, setConfirmDelete] = useState(false)
  const tagType = entry.tag_type || 'Full Mouth'
  const tags    = Array.isArray(entry.tag) ? entry.tag : []
  const meta    = tagTypeMeta(tagType)
  const isFullMouth = tagType === 'Full Mouth' || tagType === 'Ortho'
  const isAdult     = tagType === 'Adult'
  const isPedo      = tagType === 'Ped'

  function setTagType(t) {
    if (t === tagType) return
    // Save the current selection into the matching stash so it can be
    // restored if the user toggles back later without picking anything new.
    const next = { ...entry, tag_type: t }
    if (tagType === 'Adult') next._stashAdult = (tags || []).slice()
    if (tagType === 'Ped')   next._stashPed   = (tags || []).slice()
    // Restore for the destination.
    if (t === 'Adult')      next.tag = Array.isArray(next._stashAdult) ? next._stashAdult.slice() : []
    else if (t === 'Ped')   next.tag = Array.isArray(next._stashPed)   ? next._stashPed.slice()   : []
    else                    next.tag = [t]  // Ortho / Full Mouth — store the type token in tag[]
    onChange(next)
  }
  function toggleTooth(num) {
    const set = new Set(tags.map(String))
    set.has(num) ? set.delete(num) : set.add(num)
    const next = { ...entry, tag: Array.from(set).sort() }
    // Picking teeth in this category means the user is committing to it —
    // drop the other category's stash so a later switch-back doesn't resurface it.
    if (tagType === 'Adult') next._stashPed   = []
    if (tagType === 'Ped')   next._stashAdult = []
    onChange(next)
  }
  function reset() {
    const o = entry._orig || {}
    const origType  = o.tag_type || 'Full Mouth'
    const origTeeth = Array.isArray(o.tag) ? o.tag.slice() : []
    onChange({
      ...entry,
      PE:       o.PE || '',
      note:     o.note || '',
      canvas:   o.canvas || '',
      tag:      origTeeth,
      tag_type: origType,
      _stashAdult: origType === 'Adult' ? origTeeth.slice() : [],
      _stashPed:   origType === 'Ped'   ? origTeeth.slice() : [],
    })
  }
  // Show the reset button only when the entry actually diverges from its DB snapshot.
  const isDirty = (() => {
    const o = entry._orig
    if (!o) return false
    if ((o.tag_type || '') !== (tagType || '')) return true
    const a = (o.tag || []).map(String).sort()
    const b = (tags || []).map(String).sort()
    if (a.length !== b.length || a.some((v, i) => v !== b[i])) return true
    if ((o.PE   || '') !== (entry.PE   || '')) return true
    if ((o.note || '') !== (entry.note || '')) return true
    return false
  })()

  return (
    <div className="rounded-xl bg-slate-50/60 p-3.5">
      <div className="flex items-center gap-2.5 flex-wrap mb-2">
        <span className="text-[11.5px] text-slate-400 font-mono">#{index + 1}</span>

        {mode === 'view'
          ? (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[12.5px] font-semibold ring-1 ${meta.bg} ${meta.text} ${meta.ring}`}>
              {meta.label}
            </span>
          )
          : (
            <ChoiceChips
              size="sm"
              options={TAG_TYPES.map((t) => ({ value: t, label: tagTypeMeta(t).label, color: TAG_TYPE_COLOR[t] }))}
              value={tagType}
              onChange={setTagType}
            />
          )}

        {/* Teeth chips for view-only, only when applicable */}
        {mode === 'view' && !isFullMouth && tags.length > 0 && (
          <div className="flex items-center gap-1 flex-wrap">
            {tags.map((tooth) => (
              <span key={tooth} className="inline-flex items-center justify-center min-w-[26px] px-1.5 h-[22px] rounded-md bg-white ring-1 ring-slate-200 text-[12.5px] font-mono text-slate-700">
                {tooth}
              </span>
            ))}
          </div>
        )}

        {mode === 'edit' && (
          <div className="ml-auto flex items-center gap-1">
            {isDirty && (
              <Button variant="ghost" size="sm" icon={RotateCcw} onClick={reset} title="คืนค่าตามที่บันทึกไว้ในฐานข้อมูล">
                Reset
              </Button>
            )}
            <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={() => setConfirmDelete(true)} label="ลบ entry" />
          </div>
        )}
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title={`ลบ entry #${index + 1}?`}
          message="ข้อมูล tooth tag · oral examination · remark ของ entry นี้จะหายไป (ยังไม่ถูกลบจาก database จนกว่าจะกดบันทึก)"
          confirmLabel="ลบ entry"
          tone="danger"
          onConfirm={() => { setConfirmDelete(false); onRemove() }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      {/* Tooth picker for Adult / Pedo */}
      {mode === 'edit' && isAdult && (
        <div className="my-3 px-1">
          <AdultToothChart selected={tags} onToggle={toggleTooth} />
        </div>
      )}
      {mode === 'edit' && isPedo && (
        <div className="my-3 px-1">
          <PedoToothChart selected={tags} onToggle={toggleTooth} />
        </div>
      )}
      {mode === 'edit' && (tagType === 'Ortho' || tagType === 'Full Mouth') && (
        <div className="my-2 text-[12.5px] text-slate-400 italic">
          {tagType === 'Ortho' ? 'Ortho — ไม่ระบุฟัน' : 'Full Mouth — ครอบคลุมทั้งปาก'}
        </div>
      )}

      {/* Oral Examination (rich text) + Remark (plain) */}
      {mode === 'view' ? (
        <>
          {!isHtmlEmpty(entry.PE) && (
            <div className="mt-1">
              <SectionLabel>Oral Exam & Treatment</SectionLabel>
              <div
                className="text-[14px] text-slate-700 prose-sm [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(entry.PE) }}
              />
            </div>
          )}
          {entry.note && (
            <div className="mt-2">
              <SectionLabel>Remark</SectionLabel>
              <div className="text-[14px] text-slate-500 italic whitespace-pre-line">{entry.note}</div>
            </div>
          )}
          {isHtmlEmpty(entry.PE) && !entry.note && <Muted>ยังไม่มีรายละเอียด</Muted>}
        </>
      ) : (
        <div className="space-y-3 mt-2">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <SectionLabel>Oral Exam & Treatment</SectionLabel>
              <TemplateMenu
                templates={templates}
                onPick={(tpl) => onChange({ ...entry, PE: appendTemplate(entry.PE, tpl.details) })}
              />
            </div>
            <RichTextEditor
              value={entry.PE || ''}
              onChange={(v) => onChange({ ...entry, PE: v })}
              placeholder="Oral examination / สิ่งที่ตรวจ / สิ่งที่ทำ"
              minHeight={80}
            />
          </div>
          {/* REMARK field hidden per request — entry.note still persists on save */}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Templates                                                                  */
/* -------------------------------------------------------------------------- */

// Append a template BELOW the doctor's current text — never replace it.
function appendTemplate(currentPE, details) {
  const tpl = sanitizeHtml(details || '')
  if (!tpl) return currentPE || ''
  if (isHtmlEmpty(currentPE)) return tpl
  return `${currentPE}<br>${tpl}`
}

function plainPreview(html, limit = 60) {
  const text = String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  return text.length > limit ? text.slice(0, limit) + '…' : text
}

// Dropdown of the treating doctor's clinical templates. Picking one appends it.
function TemplateMenu({ templates = [], onPick }) {
  const [open, setOpen] = useState(false)
  const list = (Array.isArray(templates) ? templates : []).filter((t) => t && (t.name || t.details))
  if (list.length === 0) return null
  return (
    <div className="relative">
      <Button variant="soft" size="sm" icon={FilePlus2} onClick={() => setOpen((o) => !o)}>
        เทมเพลต
        <ChevronDown size={12} strokeWidth={2.25} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-64 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
            {list.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onPick(t); setOpen(false) }}
                className="block w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors"
              >
                <div className="text-[14px] font-medium text-slate-800 truncate">{t.name || `เทมเพลต ${i + 1}`}</div>
                {t.details && <div className="text-[12.5px] text-slate-400 truncate">{plainPreview(t.details)}</div>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                      */
/* -------------------------------------------------------------------------- */

function Block({ label, children }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <div className="text-[14.5px] text-slate-700">{children}</div>
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div className="text-[12px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1.5">{children}</div>
  )
}

function ViewField({ icon, label, children }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
        {icon}<span>{label}</span>
      </div>
      <div className="text-[14.5px] text-slate-700">{children}</div>
    </div>
  )
}

function Muted({ children }) {
  return <span className="text-slate-400 italic">{children}</span>
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function makeDraft(chart) {
  const hxArr   = Array.isArray(chart?.hx) ? chart.hx : []
  // Chart-level fields live in `chart.history` (singular). Fall back to first
  // non-empty hx[].field for older data that stored them inline.
  const history = chart?.history || {}
  const firstIn = (k) => {
    for (const x of hxArr) {
      const v = x?.[k]
      if (v != null && v !== '' && !(Array.isArray(v) && v.length === 0)) return v
    }
    return ''
  }
  const pick = (k) => {
    const v = history?.[k]
    if (v != null && v !== '') return v
    return firstIn(k)
  }
  return {
    cc:          pick('cc') || '',
    NV:          pick('NV') || '',
    NV_duration: pick('NV_duration') ?? '',
    price:       pick('price') || '',
    entries:     hxArr.map((e) => {
      const { tag_type, teeth } = normalizeHxTag(e)
      const PE   = toHtml(e.PE || '')
      const note = e.note || ''
      // _orig is the full DB snapshot — used by the per-entry "reset" button.
      // _stashAdult / _stashPed remember teeth picked for each numeric category
      // so switching tag-type back and forth doesn't drop unsaved selections.
      return {
        PE, note,
        canvas:   e.canvas || '',
        tag:      teeth,
        tag_type,
        _orig:        { PE, note, canvas: e.canvas || '', tag: teeth.slice(), tag_type },
        _stashAdult:  tag_type === 'Adult' ? teeth.slice() : [],
        _stashPed:    tag_type === 'Ped'   ? teeth.slice() : [],
      }
    }),
  }
}

function blankEntry() {
  return { PE: '', note: '', canvas: '', tag: ['Full Mouth'], tag_type: 'Full Mouth' }
}

async function patchJson(path, body) {
  const r = await fetch(path, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (r.status === 401) {
    forceLogout()
    return new Promise(() => {})
  }
  if (!r.ok) {
    const t = await r.text().catch(() => '')
    const err = new Error(`HTTP ${r.status}: ${t || r.statusText}`)
    err.status = r.status
    throw err
  }
  return r.json().catch(() => ({}))
}

